/**
 * Session complexity map — the bridge between the model's normalized band and
 * the backend's raw NWCS scores.
 *
 * At session start the client fetches every phrase's raw NWCS for the tier and
 * builds this map. The model works entirely in normalized [0, 1]; the backend
 * filters on raw NWCS [0, 100]. This class owns the conversion in both
 * directions, normalizing against the tier's ACTUAL min/max so the full [0, 1]
 * range maps onto whatever sub-range of NWCS this particular tier occupies —
 * NWCS is corpus-global, so a tier rarely spans the whole 1-100.
 *
 * v1 is static per session, but the interface is deliberately replaceable: call
 * {@link SessionComplexityMap.load} again to swap the underlying scores without
 * anything else changing.
 */

/** One phrase's raw complexity, as returned by GET /phrases/tier-complexity. */
export interface TierComplexityScore {
  s3_music_xml_id: string;
  /** Raw NWCS in [1, 100], or null when the phrase has not been scored. */
  nwcs: number | null;
}

/** A complexity band in raw NWCS units, ready to send to the backend. */
export interface NwcsBand {
  min: number;
  max: number;
}

export class SessionComplexityMap {
  /** Sorted, de-duplicated raw NWCS values for the tier. */
  private scores: number[] = [];
  private minScore = 0;
  private maxScore = 0;
  private loaded = false;

  /**
   * (Re)build the map from a tier's scores.
   *
   * Phrases with a null NWCS are ignored — they cannot be complexity-targeted,
   * and the backend excludes them from banded queries anyway. If fewer than two
   * distinct scored phrases remain, the map stays unloaded: there is no range to
   * normalize against, so complexity filtering is simply disabled for the
   * session rather than collapsing every band onto a single phrase.
   */
  load(scores: readonly TierComplexityScore[]): void {
    const values = scores
      .map((score) => score.nwcs)
      .filter((nwcs): nwcs is number => nwcs !== null && Number.isFinite(nwcs));

    const distinct = Array.from(new Set(values)).sort((a, b) => a - b);
    if (distinct.length < 2) {
      this.scores = [];
      this.loaded = false;
      return;
    }

    this.scores = distinct;
    this.minScore = distinct[0];
    this.maxScore = distinct[distinct.length - 1];
    this.loaded = true;
  }

  /** True once a usable range has been loaded. */
  isLoaded(): boolean {
    return this.loaded;
  }

  /** Number of distinct scored phrases backing the map. */
  size(): number {
    return this.scores.length;
  }

  /**
   * Convert a raw NWCS score to normalized [0, 1] against the tier's range.
   *
   * @throws if the map is not loaded.
   */
  normalize(nwcs: number): number {
    if (!this.loaded) throw new Error('SessionComplexityMap: normalize before load');
    const span = this.maxScore - this.minScore;
    return clamp01((nwcs - this.minScore) / span);
  }

  /**
   * Convert a normalized value in [0, 1] to a raw NWCS score.
   *
   * @throws if the map is not loaded, or the input is out of range.
   */
  denormalize(normalized: number): number {
    if (!this.loaded) throw new Error('SessionComplexityMap: denormalize before load');
    if (normalized < -1e-9 || normalized > 1 + 1e-9) {
      throw new Error(`normalized value ${normalized} out of [0, 1]`);
    }
    const span = this.maxScore - this.minScore;
    return this.minScore + clamp01(normalized) * span;
  }

  /**
   * Convert a normalized band to a raw NWCS band for the backend, or
   * `undefined` when the map is not loaded (complexity filtering disabled).
   *
   * The bounds are ordered defensively so a swapped pair still describes a valid
   * range.
   */
  denormalizeBand(crMin: number, crMax: number): NwcsBand | undefined {
    if (!this.loaded) return undefined;
    const a = this.denormalize(crMin);
    const b = this.denormalize(crMax);
    return { min: Math.min(a, b), max: Math.max(a, b) };
  }
}

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
